import { AdminLayout } from "./AdminLayout";
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Plus, Mail, ExternalLink } from "lucide-react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";

const MockupLeadDetail = () => {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [tab, setTab] = useState('overview');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '', goal: '', pages: 'Home,About,Services,Contact',
    style_refs: '', must_haves: '', notes_internal: '', loom_url: ''
  });

  useEffect(() => {
    fetchLead();
  }, [id]);

  const fetchLead = async () => {
    try {
      const response = await axios.get(`${API}/client-mockups/leads/${id}`, { withCredentials: true });
      setLead(response.data);
    } catch (error) {
      toast.error('Failed to fetch lead');
    }
  };

  const updateLeadStatus = async (status) => {
    try {
      await axios.patch(`${API}/client-mockups/leads/${id}`, { status }, { withCredentials: true });
      toast.success('Status updated');
      fetchLead();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const createMockupRequest = async (e) => {
    e.preventDefault();
    try {
      const data = {
        lead_id: id,
        title: formData.title,
        goal: formData.goal,
        pages: formData.pages.split(',').map(p => p.trim()),
        style_refs: formData.style_refs ? formData.style_refs.split(',').map(r => r.trim()) : [],
        must_haves: formData.must_haves ? formData.must_haves.split(',').map(m => m.trim()) : [],
        notes_internal: formData.notes_internal,
        loom_url: formData.loom_url || null
      };
      
      await axios.post(`${API}/client-mockups/mockups`, data, { withCredentials: true });
      toast.success('Mockup request created');
      setIsDialogOpen(false);
      fetchLead();
    } catch (error) {
      toast.error('Failed to create mockup request');
    }
  };

  if (!lead) return <AdminLayout title="Lead"><div className="text-center py-12">Loading...</div></AdminLayout>;

  return (
    <AdminLayout title="Lead Detail">
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold">{lead.company_name}</h2>
            <p className="text-sm text-[#8A8A8A]">{lead.contact_name} • {lead.contact_email}</p>
          </div>
          <select
            value={lead.status}
            onChange={(e) => updateLeadStatus(e.target.value)}
            className="input-field w-48"
          >
            <option value="NEW">New</option>
            <option value="CONTACTED">Contacted</option>
            <option value="MOCKUP_REQUESTED">Mockup Requested</option>
            <option value="MOCKUP_SENT">Mockup Sent</option>
            <option value="VIEWED">Viewed</option>
            <option value="FEEDBACK">Feedback</option>
            <option value="PROPOSAL">Proposal</option>
            <option value="WON">Won</option>
            <option value="LOST">Lost</option>
          </select>
        </div>

        <div className="flex gap-2 border-b border-[#2E2E2E]">
          <button
            onClick={() => setTab('overview')}
            className={`px-4 py-2 text-sm ${tab === 'overview' ? 'text-[#F5F4EF] border-b-2 border-[#5F8FB8]' : 'text-[#8A8A8A]'}`}
          >
            Overview
          </button>
          <button
            onClick={() => setTab('mockups')}
            className={`px-4 py-2 text-sm ${tab === 'mockups' ? 'text-[#F5F4EF] border-b-2 border-[#5F8FB8]' : 'text-[#8A8A8A]'}`}
          >
            Mockups ({lead.mockup_requests?.length || 0})
          </button>
          <button
            onClick={() => setTab('emails')}
            className={`px-4 py-2 text-sm ${tab === 'emails' ? 'text-[#F5F4EF] border-b-2 border-[#5F8FB8]' : 'text-[#8A8A8A]'}`}
          >
            Emails ({lead.email_logs?.length || 0})
          </button>
        </div>

        {tab === 'overview' && (
          <div className="grid grid-cols-2 gap-4">
            <div className="card-dashboard">
              <div className="text-sm text-[#8A8A8A] mb-1">Website</div>
              <div>{lead.website_url || '-'}</div>
            </div>
            <div className="card-dashboard">
              <div className="text-sm text-[#8A8A8A] mb-1">Industry</div>
              <div>{lead.industry || '-'}</div>
            </div>
          </div>
        )}

        {tab === 'mockups' && (
          <div className="space-y-4">
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <button className="btn-primary text-sm flex items-center gap-2">
                  <Plus className="w-4 h-4" /> Create Mockup Request
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#1C1C1C] border-[#2E2E2E] max-w-2xl">
                <DialogHeader><DialogTitle>Create Mockup Request</DialogTitle></DialogHeader>
                <form onSubmit={createMockupRequest} className="space-y-4 mt-4">
                  <input type="text" placeholder="Title *" required value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})} className="input-field" />
                  <textarea rows={2} placeholder="Goal *" required value={formData.goal}
                    onChange={(e) => setFormData({...formData, goal: e.target.value})} className="input-field resize-none" />
                  <input type="text" placeholder="Pages (comma-separated)" value={formData.pages}
                    onChange={(e) => setFormData({...formData, pages: e.target.value})} className="input-field" />
                  <input type="text" placeholder="Style References (URLs, comma-separated)" value={formData.style_refs}
                    onChange={(e) => setFormData({...formData, style_refs: e.target.value})} className="input-field" />
                  <input type="text" placeholder="Must Haves (comma-separated)" value={formData.must_haves}
                    onChange={(e) => setFormData({...formData, must_haves: e.target.value})} className="input-field" />
                  <textarea rows={3} placeholder="Internal Notes" value={formData.notes_internal}
                    onChange={(e) => setFormData({...formData, notes_internal: e.target.value})} className="input-field resize-none" />
                  <input type="url" placeholder="Loom URL (optional)" value={formData.loom_url}
                    onChange={(e) => setFormData({...formData, loom_url: e.target.value})} className="input-field" />
                  <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary text-sm">Cancel</button>
                    <button type="submit" className="btn-primary text-sm">Create</button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {lead.mockup_requests?.map((req) => (
              <div
                key={req.id}
                onClick={() => window.location.href = `/admin/client-mockups/mockups/${req.id}`}
                className="card-dashboard cursor-pointer hover:border-[#5F8FB8]"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium mb-1">{req.title}</h4>
                    <p className="text-sm text-[#8A8A8A]">{req.goal}</p>
                  </div>
                  <span className="badge bg-blue-500/20 text-blue-400 text-xs">{req.status}</span>
                </div>
              </div>
            )) || <p className="text-[#8A8A8A] text-center py-8">No mockup requests yet</p>}
          </div>
        )}

        {tab === 'emails' && (
          <div className="space-y-3">
            {lead.email_logs?.map((log) => (
              <div key={log.id} className="card-dashboard">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium mb-1">{log.subject}</div>
                    <div className="text-sm text-[#8A8A8A]">{log.to_email}</div>
                    <div className="text-xs text-[#8A8A8A] mt-1">
                      {new Date(log.created_at).toLocaleString()}
                    </div>
                  </div>
                  <span className={`badge ${log.status === 'SENT' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'} text-xs`}>
                    {log.status}
                  </span>
                </div>
              </div>
            )) || <p className="text-[#8A8A8A] text-center py-8">No emails sent yet</p>}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default MockupLeadDetail;
